/**
 * @changelog
 *
 * 2026-10-10 (0.1.0) (jhoward)
 *   - Add trackChatbotEvent(), the GA4 events for the Help chat: whether guests open it, what they ask about (by
 *     kind, never by text), and whether the answers help them.
 *
 * Every parameter here is registered in GA4 from App\Analytics\Ga4CustomDefinitions (`php artisan
 * ga4:sync-definitions`). Add a new parameter there in the same change, or GA4 collects it but cannot report on it.
 */

/**
 * Every Help chat event, with its parameters. The question a guest typed is never sent: it can hold their name,
 * account number or worse. Only fixed values go to GA - kinds, card types, our own suggested-question text, paths.
 */
export type ChatbotEvents = {
  /** The Help panel opened. */
  chatbot_open: { bot: ChatbotName };
  /** The Help panel closed, how, and how many questions were answered while it was open. */
  chatbot_close: { bot: ChatbotName; questions_asked: number; close_reason: ChatbotCloseReason };
  /** A suggested-question chip was clicked (its fixed text, not anything the guest typed). */
  chatbot_suggestion_click: { bot: ChatbotName; suggestion: string };
  /** A question was sent. */
  chatbot_question: { bot: ChatbotName; source: 'typed' | 'suggestion'; question_length: number };
  /** An answer came back: what kind, which cards, how long it took. */
  chatbot_answer: {
    bot: ChatbotName;
    kind: string;
    cards: string;
    article_count: number;
    has_note: boolean;
    handoff: boolean;
    pii_removed: boolean;
    response_ms: number;
  };
  /** The question could not be answered at all (an error, not a no_match). */
  chatbot_error: { bot: ChatbotName; status: number };
  /** "Was this helpful?" */
  chatbot_feedback: { bot: ChatbotName; kind: string; helpful: 'yes' | 'no' };
  /** An FAQ article in an answer was opened. */
  chatbot_article_open: { bot: ChatbotName; article_id: number };
  /** A link in an answer was followed: a login-help page, a card's page, or one offer's page. */
  chatbot_link_click: { bot: ChatbotName; link_type: ChatbotLinkType; link_path: string };
  /** An offer action button (Redeem, Cancel, Book Room) was clicked; the Offers page's own flow takes over. */
  chatbot_offer_action: { bot: ChatbotName; action: string; offer_code: string; offer_domain: string };
  /** The guest chose to reach a person. */
  chatbot_handoff: { bot: ChatbotName; method: 'contact_page' | 'phone' };
};

/** Which bot answered: the FAQ bot (everyone) or the account bot (signed-in guests). */
export type ChatbotName = 'faq' | 'account';

/** close_button: the panel's X. link: the guest followed a link or the hand-off out of the panel. */
export type ChatbotCloseReason = 'close_button' | 'link';

/** answer_link: a login-help page. card_link: a card's "see all" page. offer_link: one offer's own page. */
export type ChatbotLinkType = 'answer_link' | 'card_link' | 'offer_link';

/**
 * window.gtag is defined by resources/views/app.blade.php whether or not GA4 is configured, but this is wrapped
 * anyway: analytics must never break the chat.
 */
export default function trackChatbotEvent<Name extends keyof ChatbotEvents>(
  name: Name,
  params: ChatbotEvents[Name],
): void {
  try {
    window.gtag('event', name, { event_category: 'chatbot', ...params });
  } catch {
    //Analytics is best-effort.
  }
}
